import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IonInput, IonItem, IonList } from '@ionic/react';
import { Controller } from 'react-hook-form';
import classnames from 'classnames';
import useTheme from '../../../../../hooks/ui/useTheme';
import { usePlacesWidget } from 'react-google-autocomplete';
import { GooglePlaceResource } from '../../../../../services/resources/GooglePlaceResource';
import { useSelector } from 'react-redux';
import useDeliveryForm from './useDeliveryForm';
import { DeliveryAddressSchema } from './schema';
import useGetDeliveryLocation from '../../../common/hooks/useGetDeliveryLocation';
import SearchIcon from '../../../../../assets/icons/actions/SearchIcon';
import { useTranslation } from 'react-i18next';
import { selectCompany } from '../../../../../store/slices/InitialDataSlice';
import useGoogleMaps from '../../../../../hooks/location/useGoogleMaps';

interface DeliveryAddressFormProps {
  defaultValues?: DeliveryAddressSchema;
  validationError?: string;
  onSubmit?: (data: DeliveryAddressSchema) => void;
}

export interface HandleDeliveryFormRef {
  submit: () => void;
}

interface FormItem {
  key: keyof DeliveryAddressSchema;
  placeholder: string;
}

const formItems: FormItem[] = [
  {
    key: 'delivery_street_name',
    placeholder: 'placeholders.delivery_street_name',
  },
  { key: 'delivery_street_number', placeholder: 'placeholders.delivery_street_number' },
  { key: 'delivery_city', placeholder: 'placeholders.delivery_city' },
  { key: 'delivery_zip_code', placeholder: 'placeholders.delivery_zip_code' },
];

interface AddressSearchInputProps {
  placeholder: string;
  country?: string;
  bounds?: google.maps.LatLngBoundsLiteral | null;
  defaultValue?: string;
  autoFocus?: boolean;
  onPlaceSelected: (place: google.maps.places.PlaceResult) => void;
}

// Owns the Places widget so it can mount only once Maps has loaded: usePlacesWidget binds to the
// input exactly once, on mount, and never retries if `google.maps.places` was not there yet.
function AddressSearchInput({
  placeholder,
  country,
  bounds,
  defaultValue,
  autoFocus,
  onPlaceSelected,
}: AddressSearchInputProps) {
  const { ref } = usePlacesWidget({
    onPlaceSelected,
    options: {
      types: [],
      ...(bounds ? { bounds, strictBounds: false } : country && { componentRestrictions: { country } }),
    },
  });

  return (
    <input
      data-test="mobile-address-search-input"
      className={searchInputStyle}
      placeholder={placeholder}
      defaultValue={defaultValue}
      autoFocus={autoFocus}
      ref={ref}
    />
  );
}

const DeliveryAddressForm = forwardRef<HandleDeliveryFormRef, DeliveryAddressFormProps>(
  ({ defaultValues, onSubmit }, formRef) => {
    const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:DeliveryAddress:DeliveryAddressForm' });
    const company = useSelector(selectCompany);
    // Maps loads only once the customer starts using the search (focus or first keystroke). Until
    // then, and if Maps can't load, a plain input keeps what they typed; the fields below always work.
    const [addressSearch, setAddressSearch] = useState<string>('');
    const [hasUsedAddressSearch, setHasUsedAddressSearch] = useState<boolean>(false);
    const [isAddressSearchFocused, setIsAddressSearchFocused] = useState<boolean>(false);
    const {
      isLoaded: isGoogleMapsLoaded,
      loadError: googleMapsLoadError,
      retry: retryGoogleMaps,
    } = useGoogleMaps({ enabled: hasUsedAddressSearch });
    const form = useDeliveryForm({ defaultValues });
    const { getLocation, validationError, loading } = useGetDeliveryLocation(company);
    const theme = useTheme();
    const selectedPlace = useRef<GooglePlaceResource>(null);
    const autocompleteBounds = GooglePlaceResource.autocompleteBounds([{ lat: company?.lat, lng: company?.lon }]);

    const onPlaceSelected = (place: google.maps.places.PlaceResult): void => {
      const selectedPlaceResource = new GooglePlaceResource(place);

      form.setValue('delivery_street_name', selectedPlaceResource.getStreetName());
      form.setValue('delivery_street_number', selectedPlaceResource.getStreetNumber());
      form.setValue('delivery_city', selectedPlaceResource.getCity());
      form.setValue('delivery_zip_code', selectedPlaceResource.getPostalCode());
      form.setValue('delivery_country', selectedPlaceResource.getCountry());
      form.setValue('delivery_lat', selectedPlaceResource.getLat());
      form.setValue('delivery_lng', selectedPlaceResource.getLng());

      selectedPlace.current = selectedPlaceResource;
    };

    const onSubmitForm = async (data: DeliveryAddressSchema): Promise<void> => {
      if (loading) {
        return;
      }

      const { success, country } = await getLocation(data, selectedPlace.current);

      if (!success) {
        return;
      }

      // A hand-typed address carries the country it resolved to, not a stale or assumed one.
      form.setValue('delivery_country', country ?? null);

      onSubmit && onSubmit({ ...data, delivery_country: country ?? null });
    };

    useImperativeHandle(
      formRef,
      () => ({
        submit: form.handleSubmit(onSubmitForm),
      }),
      [form.handleSubmit],
    );

    return (
      <>
        <form onSubmit={form.handleSubmit(onSubmitForm)} className="py-md space-y-md">
          <IonList lines="none" className="space-y-md">
            <IonItem>
              <fieldset className="bordered border border-brand-primary size-full flex items-center px-md">
                {isGoogleMapsLoaded ? (
                  <AddressSearchInput
                    placeholder={t('placeholders.type_your_address')}
                    country={company?.country}
                    bounds={autocompleteBounds}
                    defaultValue={addressSearch}
                    autoFocus={isAddressSearchFocused}
                    onPlaceSelected={onPlaceSelected}
                  />
                ) : (
                  <input
                    data-test="mobile-address-search-input"
                    className={searchInputStyle}
                    placeholder={t('placeholders.type_your_address')}
                    value={addressSearch}
                    onFocus={() => {
                      setHasUsedAddressSearch(true);
                      setIsAddressSearchFocused(true);
                    }}
                    onBlur={() => setIsAddressSearchFocused(false)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setHasUsedAddressSearch(true);
                      setAddressSearch(e.target.value);
                    }}
                  />
                )}
                <label>
                  <SearchIcon className="size-4 text-sm" color={theme.color.brand.primary} />
                </label>
              </fieldset>
            </IonItem>
            {googleMapsLoadError && (
              <p data-test="mobile-address-search-maps-error" className={mapsErrorStyle}>
                {t('errors.address_suggestions_unavailable')}{' '}
                <button type="button" onClick={retryGoogleMaps} className="font-medium underline">
                  {t('buttons.retry')}
                </button>
              </p>
            )}
            {formItems.map((item) => (
              <IonItem className="overflow-visible" key={item.key}>
                <Controller
                  name={item.key}
                  control={form.control}
                  render={({ field, fieldState }) => {
                    // See the web form: a hand edit invalidates the picked place, its coordinates
                    // and its country, so the address is geocoded afresh on submit.
                    const onChange = (event: unknown) => {
                      field.onChange(event);
                      form.setValue('delivery_country', null);
                      selectedPlace.current = null;
                    };

                    return (
                      <div className="w-full">
                        <IonInput
                          type="email"
                          className={classnames('bordered !px-4', {
                            'border-danger border-xs': !!fieldState.error?.message,
                          })}
                          style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                          placeholder={t(item.placeholder)}
                          {...field}
                          onIonChange={onChange}
                          onChange={onChange}
                        />
                        <p className="text-danger text-xs h-3">{fieldState.error?.message}</p>
                      </div>
                    );
                  }}
                />
              </IonItem>
            ))}
          </IonList>
          <p className="text-danger text-xs h-3 text-center px-md">{validationError}</p>
        </form>
      </>
    );
  },
);

export default DeliveryAddressForm;

const searchInputStyle = 'w-full placeholder:text-muted-foreground';
const mapsErrorStyle = 'px-md text-xs text-brand-text-primary';
